import React, { useRef, useState } from "react";
import { shareApi } from "../../lib/api";
import { Card } from "../shared";
import { Download, FileText, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";

export const DOC_CATEGORIES = ["PPM", "Financials", "Tax Returns", "Client Files", "Legal", "Other"];
const fmtSize = (n) => (n > 1e6 ? `${(n / 1e6).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1e3))} KB`);
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "");

export function DocsManager({ share, onChange }) {
  const [cat, setCat] = useState("PPM");
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const docs = share.docs || [];

  const upload = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      let next = docs;
      for (const f of Array.from(files)) {
        const meta = await shareApi.uploadDoc(share.id, f, cat, files.length === 1 ? label : "");
        next = [...next, meta];
      }
      onChange({ ...share, docs: next });
      setLabel("");
      toast.success(`${files.length} file${files.length === 1 ? "" : "s"} uploaded`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const remove = async (did) => {
    await shareApi.deleteDoc(share.id, did);
    onChange({ ...share, docs: docs.filter((d) => d.id !== did) });
    toast.success("Document removed");
  };

  return (
    <div data-testid="docs-manager">
      <div className="docs-upload">
        <select className="inp" value={cat} onChange={(e) => setCat(e.target.value)} data-testid="doc-category">
          {DOC_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
        </select>
        <input className="inp" placeholder="Label (optional, e.g. PPM Draft v2)" value={label} onChange={(e) => setLabel(e.target.value)} data-testid="doc-label" />
        <input ref={fileRef} type="file" multiple style={{ display: "none" }} onChange={(e) => upload(e.target.files)} data-testid="doc-file-input" />
        <button className="btn btn-primary" disabled={busy} onClick={() => fileRef.current?.click()} data-testid="doc-upload-btn"><Upload size={13} style={{ marginRight: 6 }} />{busy ? "Uploading…" : "Upload files"}</button>
      </div>
      <div className="ra-sub" style={{ marginBottom: 8 }}>Any file type, 25 MB each. Investors download from the room's Documents tab; downloads are tracked per investor.</div>
      {docs.length === 0 && <div className="bullet">No documents yet.</div>}
      {DOC_CATEGORIES.filter((c) => docs.some((d) => d.category === c)).map((c) => (
        <div key={c}>
          <div className="section-label">{c}</div>
          {docs.filter((d) => d.category === c).map((d) => (
            <div className="doc-row" key={d.id} data-testid={`doc-${d.id}`}>
              <FileText size={14} />
              <div className="doc-main"><b>{d.label}</b><span className="ra-sub">{d.filename} · {fmtSize(d.size)} · {fmtDate(d.uploadedAt)} · {d.downloads || 0} download{d.downloads === 1 ? "" : "s"}</span></div>
              <button className="icon-btn" onClick={() => remove(d.id)} title="Remove" data-testid={`doc-del-${d.id}`}><Trash2 size={11} /></button>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

export function DocumentsTab({ shareId, sessionId, docs }) {
  const list = docs || [];
  if (!list.length) return <Card title="Documents"><div className="bullet">No documents have been shared yet. Check back after the sponsor uploads the PPM and supporting files.</div></Card>;
  return (
    <div className="grid g2">
      {DOC_CATEGORIES.filter((c) => list.some((d) => d.category === c)).map((c) => (
        <Card title={c} key={c} testid={`ir-docs-${c.replace(/\s+/g, "-").toLowerCase()}`}>
          {list.filter((d) => d.category === c).map((d) => (
            <a className="doc-row link" key={d.id} href={shareApi.docUrl(shareId, d.id, sessionId)} target="_blank" rel="noreferrer" data-testid={`ir-doc-${d.id}`}>
              <FileText size={14} />
              <div className="doc-main"><b>{d.label}</b><span className="ra-sub">{d.filename} · {fmtSize(d.size)} · {fmtDate(d.uploadedAt)}</span></div>
              <Download size={14} />
            </a>
          ))}
        </Card>
      ))}
    </div>
  );
}
